import { useQuery } from "@tanstack/react-query";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import {
  Users,
  Crown,
  Scales,
  Cow,
  CurrencyDollar,
  ChartLineUp,
  Sparkle,
  Prohibit,
} from "@phosphor-icons/react";
import { api, formatBRL, formatNum } from "@/lib/gado";

function KpiCard({ icon: Icon, label, value, sub, accent = false, testid }) {
  return (
    <div
      className="rounded-xl border border-gado-line bg-white p-4 min-w-0"
      data-testid={testid}
    >
      <div className="flex items-center gap-2 mb-1.5">
        <span className={`h-7 w-7 rounded-lg grid place-items-center shrink-0 ${accent ? "bg-gado-gold/15 text-gado-gold" : "bg-gado-green/10 text-gado-green"}`}>
          <Icon size={15} weight="fill" />
        </span>
        <p className="text-[10px] uppercase tracking-[0.14em] text-gado-muted font-semibold truncate">{label}</p>
      </div>
      <p className="font-cabinet text-xl md:text-2xl font-extrabold text-gado-ink tabular-nums truncate">{value}</p>
      {sub && <p className="text-[11px] text-gado-muted mt-0.5 truncate">{sub}</p>}
    </div>
  );
}

function ChartCard({ title, data, color, testid }) {
  return (
    <div className="rounded-xl border border-gado-line bg-white p-4 md:p-5" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-[0.2em] text-gado-muted font-semibold mb-3">{title}</p>
      <div className="h-44 md:h-52 min-w-[1px]">
        <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
          <BarChart data={data} margin={{ top: 4, right: 4, left: -22, bottom: 0 }}>            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#EDEFEA" />
            <XAxis dataKey="mes" tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: "#5C6A60" }} />
            <YAxis allowDecimals={false} tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: "#5C6A60" }} />
            <Tooltip
              cursor={{ fill: "#F3F5F1" }}
              contentStyle={{ borderRadius: 10, border: "1px solid #E8EAE6", fontSize: 12 }}
            />
            <Bar dataKey="valor" fill={color} radius={[6, 6, 0, 0]} maxBarSize={36} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { data, isLoading } = useQuery({
    queryKey: ["admin-overview"],
    queryFn: async () => (await api.get("/admin/overview")).data,
  });

  if (isLoading || !data) {
    return (
      <div className="space-y-4" data-testid="admin-dashboard">
        <div className="h-8 w-48 rounded-lg bg-gado-line/50 animate-pulse" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="h-24 rounded-xl bg-gado-line/40 animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  const { users, activity, pesagens, analyses_total } = data;

  return (
    <div className="space-y-5" data-testid="admin-dashboard">
      <div>
        <h1 className="font-cabinet text-2xl md:text-3xl font-bold text-gado-ink tracking-tight">Dashboard executivo</h1>
        <p className="text-gado-muted text-sm">Visão geral do GadoPro — dados em tempo real.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard icon={Users} label="Usuários" value={formatNum(users.total, 0)} sub={`+${users.new_this_month} este mês`} testid="kpi-users" />
        <KpiCard icon={Crown} label="Assinantes Pro" value={formatNum(users.pro, 0)} sub={`${users.free} no plano grátis`} accent testid="kpi-pro" />
        <KpiCard icon={Scales} label="Pesagens no mês" value={formatNum(pesagens.this_month, 0)} sub={`${formatNum(pesagens.total, 0)} no total`} testid="kpi-pesagens" />
        <KpiCard icon={Cow} label="Animais no mês" value={formatNum(pesagens.animals_this_month, 0)} sub="cabeças pesadas" testid="kpi-animais" />
        <KpiCard icon={CurrencyDollar} label="Valor comprado no mês" value={formatBRL(pesagens.amount_this_month)} sub="soma das pesagens" accent testid="kpi-valor" />
        <KpiCard icon={ChartLineUp} label="Usuários ativos" value={`${activity.dau} / ${activity.mau}`} sub="últimas 24h / 30 dias" testid="kpi-dau-mau" />
        <KpiCard icon={Sparkle} label="Análises de IA" value={formatNum(analyses_total, 0)} sub="vídeos processados" accent testid="kpi-ia" />
        <KpiCard icon={Prohibit} label="Contas bloqueadas" value={formatNum(users.blocked, 0)} sub="acesso suspenso" testid="kpi-blocked" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ChartCard title="Pesagens por mês" data={data.chart_pesagens} color="#1A472A" testid="chart-pesagens" />
        <ChartCard title="Novos usuários por mês" data={data.chart_users} color="#C8963E" testid="chart-usuarios" />
      </div>
    </div>
  );
}
